import React, { useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_SAVED_QUERIES, tokenOptions } from "../../backend/graphql";
import { useAppSelector } from "../../app/hooks";
import useConfirmNewValue from "../widgets/useConfirmNewValue";

// The sections the dashboard's saved queries are listed under, in use today. There is no table of
// sections: a section is the name its queries carry.
export const useSavedQuerySections = (): string[] => {
  const token = useAppSelector((state) => state.auth.token);
  const { data } = useQuery(GET_SAVED_QUERIES, {
    ...tokenOptions(token),
    variables: { take: 1000, skip: 0, filter: 'category == "QADashboard"' },
  });
  const sections: string[] = (data?.savedQueries?.items ?? [])
    .map((item: any) => item.section ?? "")
    .filter(Boolean);
  return Array.from(new Set(sections)).sort((a, b) => a.localeCompare(b));
};

// A saved query's section: one of the sections in use, a new one once confirmed, or none (cleared).
// A name typed in another case than an existing section is taken as that section, so one section
// keeps one name.
const SectionSelect = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (section: string) => void;
}) => {
  const sections = useSavedQuerySections();
  const [typed, setTyped] = useState(value);
  const confirmNew = useConfirmNewValue("section");

  const handleChange = async (chosen: string | null) => {
    const name = (chosen ?? "").trim();
    const existing = sections.find(
      (section) => section.toLowerCase() === name.toLowerCase(),
    );
    if (!name || existing) {
      onChange(existing ?? "");
    } else if (await confirmNew(name)) {
      onChange(name);
    } else {
      setTyped(value); // not created: the field shows the section it still has
    }
  };

  // Leaving the field keeps what was typed — never the option the list happens to have highlighted,
  // which MUI's autoSelect would pick whenever the open list loses focus.
  const handleBlur = () => {
    if (typed.trim() !== value) {
      handleChange(typed);
    }
  };

  return (
    <Autocomplete
      freeSolo
      options={sections}
      value={value || null}
      onChange={(_, chosen) => handleChange(chosen)}
      inputValue={typed}
      onInputChange={(_, input) => setTyped(input)}
      onBlur={handleBlur}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Section"
          placeholder="None, one in use, or a new section"
        />
      )}
    />
  );
};

export default SectionSelect;
